import React, { useState, ChangeEvent, FormEvent } from 'react'
import axios from 'axios'
import { API_URL, config } from '../../components/Include'
import { Alerts } from '../../components/notify'
import './Registration.css'

interface FormData {
  matricNumber: string;
  fullName: string;
  modeOfEntry: string;
  program: string;
  department: string;
  faculty: string;
  level: string;
  gender: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  state: string;
  lga: string;
  portalPin: string;
}

const Registration = () => {
  const [formData, setFormData] = useState<FormData>({
    matricNumber: '',
    fullName: '',
    modeOfEntry: '',
    program: '',
    department: '',
    faculty: '',
    level: '200 Level', // Default to 200L as specified
    gender: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    state: '',
    lga: '',
    portalPin: ''
  })

  const [errors, setErrors] = useState({
    errorMessage: '',
    isLoading: false
  })

  const [programs] = useState([
    'HND',
    'ND',
    'BSc',
    'MSc',
    'PhD'
  ])

  const [levels] = useState([
    '200 Level',
    '300 Level'
  ])
  
  const [entryModes] = useState([
    'UTME',
    'DE',
    'PT',
    'LVT'
  ])
  
  const [nigerianStates] = useState([
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River',
    'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano',
    'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
    'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
  ])
  
  const [faculties] = useState([
    'Agriculture',
    'Arts',
    'Basic Medical Sciences',
    'Business Administration',
    'Education',
    'Engineering',
    'Environmental Sciences',
    'Law',
    'Medicine',
    'Pharmacy',
    'Science',
    'Social Sciences',
    'Technology',
    'Veterinary Medicine'
  ])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    
    // Basic validation
    if (!formData.matricNumber || !formData.fullName || !formData.modeOfEntry || 
        !formData.program || !formData.department || !formData.faculty || 
        !formData.level || !formData.gender || !formData.dateOfBirth || 
        !formData.phone || !formData.state || !formData.lga || !formData.portalPin) {
      Alerts('warning', 'Please fill all required fields', 'Validation Error')
      return
    }

    // Email validation (only if provided since it's optional)
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        Alerts('warning', 'Please enter a valid email address', 'Validation Error')
        return
      }
    }

    // Phone validation
    const phoneRegex = /^[0-9]{11}$/
    if (!phoneRegex.test(formData.phone)) {
      Alerts('warning', 'Please enter a valid 11-digit phone number', 'Validation Error')
      return
    }

    setErrors({ ...errors, isLoading: true })

    try {
      const fd = new FormData()
      Object.entries(formData).forEach(([key, value]) => {
        fd.append(key, value)
      })

      const url = API_URL + '/student/registration'
      const response = await axios.post(url, fd, config)

      if (response.data.status === 200) {
        Alerts('success', response.data.message, 'Registration Successful')
        // Clear form after successful submission
        setFormData({
          matricNumber: '',
          fullName: '',
          modeOfEntry: '',
          program: '',
          department: '',
          faculty: '',
          level: '200 Level',
          gender: '',
          dateOfBirth: '',
          email: '',
          phone: '',
          state: '',
          lga: '',
          portalPin: ''
        })
      } else {
        Alerts('danger', response.data.message, 'Registration Failed')
      }
    } catch (error: any) {
      Alerts('danger', error.message, 'Something went wrong!')
    } finally {
      setErrors({ ...errors, isLoading: false })
    }
  }

  return (
    <div className="registration-container">
      {/* Loading Overlay */}
      {errors.isLoading && (
        <div className="overlay">
          <img src="/images/loader.svg" alt="loader" />
        </div>
      )}

      <div className="registration-header">
        <h2>Student Registration</h2>
        <p className="registration-subtitle">
          Complete this form to register your missing student information
        </p>
      </div>

      <div className="registration-card">
        <div className="pin-notice">
          <div className="icon-container">
            <i className="fa fa-info-circle"></i>
          </div>
          <div className="notice-text">
            <h4>Important Notice</h4>
            <p>Enter the Portal PIN provided by the administrator to complete your registration.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="matricNumber">Matriculation Number <span className="required">*</span></label>
              <input
                type="text"
                id="matricNumber"
                name="matricNumber"
                value={formData.matricNumber}
                onChange={handleChange}
                placeholder="Enter your matriculation number"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="fullName">Full Name <span className="required">*</span></label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="modeOfEntry">Mode of Entry <span className="required">*</span></label>
              <select
                id="modeOfEntry"
                name="modeOfEntry"
                value={formData.modeOfEntry}
                onChange={handleChange}
                required
              >
                <option value="">Select your mode of entry</option>
                {entryModes.map((mode, index) => (
                  <option key={index} value={mode}>{mode}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="program">Program <span className="required">*</span></label>
              <select
                id="program"
                name="program"
                value={formData.program}
                onChange={handleChange}
                required
              >
                <option value="">Select your program</option>
                {programs.map((program, index) => (
                  <option key={index} value={program}>{program}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="faculty">Faculty <span className="required">*</span></label>
              <select
                id="faculty"
                name="faculty"
                value={formData.faculty}
                onChange={handleChange}
                required
              >
                <option value="">Select your faculty</option>
                {faculties.map((faculty, index) => (
                  <option key={index} value={faculty}>{faculty}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="department">Department <span className="required">*</span></label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                placeholder="Enter your department"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="level">Level <span className="required">*</span></label>
              <select
                id="level"
                name="level"
                value={formData.level}
                onChange={handleChange}
                required
              >
                <option value="">Select your level</option>
                {levels.map((level, index) => (
                  <option key={index} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="gender">Gender <span className="required">*</span></label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">Select your gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth <span className="required">*</span></label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Institutional Email <span className="optional">(optional)</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your institutional email"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Phone Number <span className="required">*</span></label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="portalPin">Portal PIN <span className="required">*</span></label>
              <input
                type="text"
                id="portalPin"
                name="portalPin"
                value={formData.portalPin}
                onChange={handleChange}
                placeholder="Enter your portal PIN"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="state">State <span className="required">*</span></label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              >
                <option value="">Select your state</option>
                {nigerianStates.map((state, index) => (
                  <option key={index} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="lga">Local Government Area <span className="required">*</span></label>
              <input
                type="text"
                id="lga"
                name="lga"
                value={formData.lga}
                onChange={handleChange}
                placeholder="Enter your LGA"
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-register">
              <i className="fa fa-user-plus"></i> Register
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Registration
